/**
 * Design Tokens - Single Source of Truth
 * All colors, fonts, spacing values live here.
 * Import from any component: import { FONT, MONO, COLORS } from "../theme/tokens"
 */

// ===== FONTS =====
export const FONT = "'Source Sans 3', 'Source Sans Pro', sans-serif";
export const MONO = FONT;  // unified - no separate mono font
export const FONT_URL = "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@300;400;500;600;700&display=swap";

// ===== COLORS (semantic) =====
// No yellow/orange - only green/red status palette
export const COLORS = {
  accent:      "#4B7FFA",
  accentHover: "#3A6EE8",
  accentLight: "#6D96FB",
  accentGlow:  "rgba(75,127,250,.22)",
  accentSubtle:"rgba(75,127,250,.08)",
  success:     "#34D399",
  error:       "#F87171",
  // warning intentionally removed - project uses green/red only
};

// ===== DARK PALETTE =====
// Philosophy: Blue-tinted deep darks - not neutral grey.
// Each surface level is slightly lighter and has a subtle blue hue shift.
// Inspired by Linear, Vercel Dashboard, Grafana 10.
// Borders are barely-there solids rather than rgba - more crisp on OLED.
export const DARK = {
  // Canvas → surfaces: ascending lightness, Mockup blue-tinted greys
  bg:           "#111827",   // Mockup --bg - sichtbar blau-getönt statt near-black
  surface0:     "#1A2332",   // Mockup --surface-alt
  surface1:     "#1F2937",   // Mockup --surface - Cards, Panels
  surface2:     "#283548",   // interpoliert - Hover states
  surface3:     "#374151",   // Mockup --border level - Tooltips, Popovers

  // Borders - deutlich sichtbarer als vorher
  border:       "#374151",   // Mockup --border
  borderStrong: "#4B5563",   // heller für Hover/Focus
  borderSub:    "#1E2940",   // subtile Divider

  // Text - Mockup Werte
  text1:        "#F3F4F6",   // Mockup --text
  text2:        "#9CA3AF",   // Mockup --text-secondary
  text3:        "#6B7280",   // muted - etwas heller als vorher

  // Scrollbar
  scrollTrack:  "transparent",
  scrollThumb:  "rgba(75,127,250,.18)",

  // Shadows - etwas leichter passend zu helleren Surfaces
  shadow:       "0 2px 8px rgba(0,0,0,.5)",
  shadowLg:     "0 16px 48px rgba(0,0,0,.6)",
  shadowXl:     "0 24px 64px rgba(0,0,0,.7)",

  // Sidebar - etwas heller als bisher, aber dunkler als bg für Kontrast
  sidebar:      "#0F1520",
  sidebarBorder:"rgba(75,127,250,.12)",
};

// ===== LIGHT PALETTE (Warm Neutral - sand, stone, modern enterprise) =====
export const LIGHT = {
  bg:           "#F5F0EB",       // warm sand off-white - grounded, not sterile
  surface0:     "#FFFDF9",       // cards - cream white, warm and inviting
  surface1:     "#F0EBE4",       // nested surfaces - warm stone
  surface2:     "#E8E1D8",       // hover states, row highlights - tan
  surface3:     "#DDD5CA",       // selected rows, active states - deeper sand
  border:       "#D4CBBF",       // warm border - visible but soft
  borderStrong: "#BFB5A8",       // hover / active border - warm taupe
  borderSub:    "#E2DCD4",       // table dividers - barely there
  text1:        "#1A1714",       // deep warm black - maximum readability
  text2:        "#574E44",       // secondary - warm brown-gray
  text3:        "#8C8276",       // muted - medium warm gray
  scrollTrack:  "transparent",
  scrollThumb:  "#C4B9AC",
  shadow:       "0 1px 4px rgba(80,60,30,.08), 0 1px 2px rgba(80,60,30,.05)",
  shadowLg:     "0 10px 24px rgba(80,60,30,.10), 0 4px 8px rgba(80,60,30,.05)",
  shadowXl:     "0 20px 48px rgba(80,60,30,.12), 0 8px 16px rgba(80,60,30,.06)",
  // Sidebar - deep warm charcoal, not cold slate
  sidebar:      "#2C2520",
  sidebarText:  "#A89B8E",
  sidebarActive:"#F0EBE4",
  sidebarBorder:"#3D342C",
};

// ===== SPACING =====
export const SPACING = {
  ctrlH:       "36px",
  ctrlHSm:     "30px",
  ctrlHLg:     "40px",
  ctrlRadius:  "8px",
  ctrlRadiusSm:"6px",
  ctrlPx:      "12px",
  ctrlGap:     "8px",
  ctrlFont:    "13px",
  ctrlFontSm:  "12px",
  cardRadius:  "10px",
  cardPx:      "16px",
  cardPy:      "14px",
  sectionGap:  "16px",
  toolbarGap:  "10px",
  sidebarW:    240,
  sidebarCollapsed: 64,
};

// ===== STATUS COLORS =====
// Green/red only - no yellow/orange status states per project requirements
export const STATUS_COLORS = {
  COMPLETED: "success",
  STARTED:   "success",
  FAILED:    "error",
  ERROR:     "error",
  PROCESSING:"primary",
  RETRY:     "error",   // degraded → treated as error state
  ESCALATED: "error",
};
